<template>
    <App>
        <div class="layout">
            <Sidebar :links="sideNavRoutes" />
            <main>
                <slot />
            </main>
        </div>
    </App>
</template>
<script>
const pageTransitionName = "smooth-slide";

import App from "../../App.vue";

import {
    PhHouse,
    PhCirclesFour,
    PhUsers,
    PhHeadset,
    PhGear,
    PhCircleWavyCheck,
    PhSunHorizon,
    PhMusicNotes,
    PhPaperPlaneRight,
    PhWind,
    PhFlowerLotus,
    PhBarbell,
    PhArticle,
    PhPlayCircle,
    PhBooks,
    PhDeviceMobile,
    PhBrain,
    PhBug,
    PhHardDrives,
} from "phosphor-vue";

export default {
    name: "Layout",
    components: {
        App,
        Sidebar: () =>
            import(/* webpackChunkName: "layouts-admin-sidebar" */ "./sidebar"),
    },
    watch: {
        "$page.url": {
            immediate: true,
            handler(newData, oldData) {
                // don't do transition for the first page load
                if (!oldData) {
                    this.transitionName = null;
                    return;
                }

                if (this.transitionOverRidden) {
                    return;
                }

                if (newData) {
                    this.transitionName = pageTransitionName;
                }

                // clear transition name after transition end,
                // to prevent unwanted animation when modal is shown
                setTimeout(() => {
                    this.transitionName = null;
                }, 1000);
            },
        },
    },
    data() {
        return {
            transitionName: null,
            transitionOverRidden: false,
        };
    },
    mounted() {
        this.$root.$on("hold-transition", (name = null) => {
            this.transitionOverRidden = true;
            this.transitionName = name;
            setTimeout(() => {
                this.transitionOverRidden = false;
                this.transitionName = null;
            }, 1000);
        });
    },
    methods: {
        beforeTransition() {
            document.body.style.overflow = "hidden";
        },
        afterTransition() {
            document.body.style.overflow = null;
        },
    },
    computed: {
        user() {
            return this.$store.getters["auth/user"];
        },
        counts() {
            return this.$page.props.new_counts || {};
        },
        sideNavRoutes() {
            return [
                {
                    title: "Dashboard",
                    icon: PhHouse,
                    to: "admin.index",
                    deepActive: false,
                },
                {
                    title: "Contact us",
                    icon: PhPaperPlaneRight,
                    to: "admin.contactus",
                    deepActive: false,
                    badge: {
                        value: this.counts.contact_us || 0,
                        valueSuffix: " new",
                        color: "danger",
                    },
                },
                {
                    title: "Support tickets",
                    icon: PhHeadset,
                    to: "admin.support.index",
                    deepActive: true,
                    badge: {
                        value: this.counts.support_tickets || 0,
                        valueSuffix: " new",
                        color: "danger",
                    },
                },
                {
                    title: "Users",
                    icon: PhUsers,
                    to: "admin.users.index",
                    deepActive: true,
                },
                {
                    title: "Blogs",
                    icon: PhArticle,
                    to: "admin.blogs.index",
                    deepActive: true,
                },
                {
                    title: "Vlogs",
                    icon: PhPlayCircle,
                    to: "admin.vlogs.index",
                    deepActive: true,
                },
                {
                    title: "E-books",
                    icon: PhBooks,
                    to: "admin.ebooks.index",
                    deepActive: true,
                },
                {
                    title: "Blisscipline",
                    icon: PhFlowerLotus,
                    to: "#",
                    deepActive: true,
                    deepActiveRoute: "admin.blisscipline.*",
                    children: [
                        {
                            title: "Blisscipline",
                            icon: PhCirclesFour,
                            to: "admin.blisscipline.blisscipline.index",
                            deepActive: true,
                        },
                        {
                            title: "Blisscipline workouts",
                            icon: PhBarbell,
                            to: "admin.blisscipline.workouts.index",
                            deepActive: true,
                        },
                    ],
                },
                {
                    title: "Blissdom",
                    icon: PhSunHorizon,
                    to: "#",
                    deepActive: true,
                    deepActiveRoute: "admin.blissdom.*",
                    children: [
                        {
                            title: "Blissdom categories",
                            icon: PhCirclesFour,
                            to: "admin.blissdom.categories.index",
                            deepActive: true,
                        },
                        {
                            title: "Blissdom sections",
                            icon: PhCirclesFour,
                            to: "admin.blissdom.sections.index",
                            deepActive: true,
                        },
                        {
                            title: "Meditation tracks",
                            icon: PhMusicNotes,
                            to: "admin.blissdom.guidedmeditation.index",
                            deepActive: true,
                        },
                        {
                            title: "Breathing exercises",
                            icon: PhWind,
                            to: "admin.blissdom.breathingexercise.index",
                            deepActive: true,
                        },
                    ],
                },
                {
                    title: "Package details",
                    icon: PhCircleWavyCheck,
                    to: "admin.packagedetails.index",
                    deepActive: false,
                },
                {
                    title: "App splash screen",
                    icon: PhDeviceMobile,
                    to: "admin.splashscreens.index",
                    deepActive: false,
                },
                {
                    title: "Settings",
                    icon: PhGear,
                    to: "#",
                    deepActive: true,
                    deepActiveRoute: "admin.settings.*",
                    children: [
                        {
                            title: "App config",
                            icon: PhBrain,
                            to: "admin.settings.index",
                            deepActive: false,
                        },
                        {
                            title: "Error logs",
                            icon: PhBug,
                            to: "admin.settings.errorlogs",
                            deepActive: false,
                        },
                        {
                            title: "Telescope",
                            icon: PhHardDrives,
                            to: "admin.settings.telescope",
                            deepActive: false,
                        },
                        {
                            title: "Horizon",
                            icon: PhHardDrives,
                            to: "admin.settings.horizon",
                            deepActive: false,
                        },
                    ],
                },
            ];
        },
    },
};
</script>
<style lang="scss" scoped>
main {
    margin-left: 320px;
    @media (max-width: 1055px) {
        margin-left: 0;
    }
}

// smooth slide

.smooth-slide-enter-active,
.smooth-slide-leave-active {
    z-index: -10;
    position: relative;
    transition: all 0.25s cubic-bezier(0.7, 0, 0.3, 1);
}

.smooth-slide-enter-from,
.smooth-slide-leave-to {
    opacity: 0;
    transform: translateY(5%);
    @media (max-width: 1055px) {
        transform: translateX(-2%);
    }
}

.smooth-slide-active,
.smooth-slide-active {
    z-index: -1;
    position: relative;
    transition: all 0.25s cubic-bezier(0.7, 0, 0.3, 1);
    overflow: hidden;
}

.smooth-slide-enter,
.smooth-slide-leave-active {
    opacity: 0;
    transform: translateY(5%);
    @media (max-width: 1055px) {
        transform: translateX(-2%);
    }
}
</style>
